import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { type SuccessResponse, zodSuccessResponseSchema } from '@vestigas/data';

import { vestigasApi } from '~/services/kyClient';

import { log } from '~/utils';

import { queryKeysCompany } from './queryKeys';

type UploadCompanyLogoArguments = {
  companyId: UUID;
  logoImage: Blob;
};

/**
 * Updates a company logo.
 *
 * @param {string} companyId - The ID of the company for which to update the logo.
 * @param {Object} logoImage - A Blob representing the logo image.
 * @returns {Promise} A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Company/operation/upload_company_logo_company__id__logo_put
 */
// fallow-ignore-next-line unused-export
export const uploadCompanyLogo = async (companyId: UUID, logoImage: Blob) => {
  try {
    return await vestigasApi
      .put(ENDPOINT.COMPANY.LOGO.UPDATE(companyId), {
        body: logoImage,
        headers: { 'Content-Type': logoImage.type },
        validate: { response: zodSuccessResponseSchema },
      })
      .json<SuccessResponse>();
  } catch (error) {
    log.error({
      message: 'Error updating company logo',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};

/**
 * React Query mutation hook for uploading a company logo.
 *
 * This hook provides functionality to:
 * - Upload a new logo image for a company
 * - Automatically invalidate the company query cache on completion
 *
 * @returns {UseMutationResult} A mutation object containing the upload function and status
 *
 * @example
 * const mutation = useMutationCompanyLogoUpload(companyId);
 * mutation.mutate(logoImageBlob);
 */
export const useMutationCompanyLogoUpload = (
  options?: Omit<
    UseMutationOptions<SuccessResponse, Error, UploadCompanyLogoArguments>,
    'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation<SuccessResponse, Error, UploadCompanyLogoArguments>({
    mutationFn: async ({ companyId, logoImage }: UploadCompanyLogoArguments) =>
      uploadCompanyLogo(companyId, logoImage),
    onSettled(_, __, { companyId }) {
      queryClient.invalidateQueries({
        queryKey: queryKeysCompany.getAdmin(companyId),
      });
    },
    ...options,
  });
};
